import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  CartesianGrid,
} from 'recharts';
import type { SpendSeriesPoint } from './budgetAnalytics';

export interface SpendChartProps {
  points: SpendSeriesPoint[];
  currentYearLabel: string;
  previousYearLabel: string;
  twoYearsAgoLabel: string;
}

function formatCurrency(value: number): string {
  // Keep it simple, avoid i18n complexity for v1.
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

export const SpendChart: React.FC<SpendChartProps> = ({
  points,
  currentYearLabel,
  previousYearLabel,
  twoYearsAgoLabel,
}) => {
  return (
    <ResponsiveContainer width="100%" height={260}>
      <LineChart data={points} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="dayIndex"
          tick={{ fontSize: 12 }}
          tickFormatter={(dayIndex: number) => {
            const point = points.find(p => p.dayIndex === dayIndex);
            return point?.label || '';
          }}
          ticks={points.filter(p => p.label).map(p => p.dayIndex)}
        />
        <YAxis tick={{ fontSize: 12 }} tickFormatter={formatCurrency} />
        <Tooltip
          formatter={(value: unknown) => {
            const n = typeof value === 'number' ? value : Number(value);
            return formatCurrency(Number.isFinite(n) ? n : 0);
          }}
          labelFormatter={(dayIndex: number) => {
            const point = points.find(p => p.dayIndex === dayIndex);
            return point?.label || `Day ${dayIndex + 1}`;
          }}
        />
        <Legend />
        <Line
          type="monotone"
          dataKey="current"
          name={currentYearLabel}
          stroke="#3FB950"
          strokeWidth={2}
          dot={false}
          connectNulls={false}
        />
        <Line
          type="monotone"
          dataKey="previous"
          name={previousYearLabel}
          stroke="#4A90D9"
          strokeWidth={2}
          dot={false}
        />
        <Line
          type="monotone"
          dataKey="twoYearsAgo"
          name={twoYearsAgoLabel}
          stroke="#9333EA"
          strokeWidth={2}
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

